import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { NAV, AREAS, SITEMAP, LASER_MEGA, SKIN_MEGA } from "@/content/site";

export function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="main-nav"
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,backdrop-filter,border-color,padding] duration-500 ${
        scrolled ? "bg-canvas/80 backdrop-blur-xl border-b border-ink/10 py-4" : "bg-canvas/40 backdrop-blur-md py-5 border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 shrink-0 group">
          <img src="/logo-mark.png" alt="Logo Dott. Alessandro Valle" className="h-12 md:h-16 w-auto" />
          <span className="flex flex-col leading-none">
            <span className="text-[10px] md:text-[11px] uppercase tracking-[0.5em] text-sagedark mb-1.5 pl-[2px] transition-colors duration-300 group-hover:text-ink">Dott.</span>
            <span className="font-serif text-xl md:text-[27px] font-light tracking-[0.01em] leading-none">Alessandro <span className="italic text-sagedark transition-colors duration-300 group-hover:text-ink">Valle</span></span>
          </span>
        </Link>

        <nav className="hidden xl:flex items-center gap-4 2xl:gap-5 mx-6 2xl:mx-10" onMouseLeave={() => setHover(null)}>
          {NAV.map((item) => {
            const area = item.area ? AREAS[item.area] : null;
            const hasSubs = area && area.subs && area.subs.length > 0;
            const hasExtra = item.extra && item.extra.length > 0;
            const hasMega = !!item.mega;
            const megaCols = item.mega === "skin" ? SKIN_MEGA : LASER_MEGA;
            const hasMenu = hasSubs || hasExtra || hasMega;
            return (
              <div key={item.to} className="relative" onMouseEnter={() => setHover(item.to)}>
                <Link
                  to={item.to}
                  data-testid={`nav-link-${item.to}`}
                  className={`link-underline flex items-center gap-1 whitespace-nowrap text-sm py-2 ${item.highlight ? "text-sagedark font-medium hover:text-ink" : "text-ink/80 hover:text-ink"}`}
                >
                  {item.highlight && <span className="w-1.5 h-1.5 rounded-full bg-sagedark" aria-hidden="true" />}
                  {item.label}
                  {hasMenu && <ChevronDown className="w-3.5 h-3.5 opacity-60" />}
                </Link>
                <AnimatePresence>
                  {hasMenu && hover === item.to && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 8 }}
                      transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                      className={hasMega ? "fixed inset-x-0 top-[68px] pt-3 flex justify-center px-4" : "absolute left-0 top-full pt-3"}
                    >
                      {hasMega ? (
                        <div className="bg-canvas border border-ink/10 shadow-[0_24px_50px_-24px_rgba(0,0,0,0.3)] w-[min(92vw,1000px)] p-7">
                          <div className="grid grid-cols-2 lg:grid-cols-5 gap-x-6 gap-y-6">
                            {megaCols.map((col) => (
                              <div key={col.group}>
                                <p className="text-[10px] uppercase tracking-[0.2em] text-sagedark mb-3 pb-2 border-b border-ink/10">{col.group}</p>
                                <div className="flex flex-col gap-0.5">
                                  {col.items.map((it) => (
                                    <Link key={it.label} to={it.to} data-testid={`nav-mega-${it.label}`} className="block py-1.5 text-[13px] leading-snug text-ink/75 hover:text-ink transition-colors duration-200">
                                      {it.label}
                                    </Link>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                          <Link to={item.to} className="inline-block mt-6 pt-4 border-t border-ink/10 w-full text-[11px] uppercase tracking-[0.15em] text-sagedark hover:text-ink transition-colors">
                            Panoramica {item.label} →
                          </Link>
                        </div>
                      ) : (
                      <div className="bg-canvas border border-ink/10 shadow-[0_20px_40px_-20px_rgba(0,0,0,0.25)] min-w-[280px] p-2">
                        {hasSubs && area.subs.map((s) => (
                          <Link
                            key={s.slug}
                            to={`${item.to}/${s.slug}`}
                            className="block px-4 py-2.5 text-sm text-ink/75 hover:text-ink hover:bg-sand transition-colors duration-200"
                          >
                            {s.title}
                          </Link>
                        ))}
                        {hasExtra && item.extra.map((e) => (
                          <Link
                            key={e.to}
                            to={e.to}
                            data-testid={`nav-extra-${e.to}`}
                            className="block px-4 py-2.5 text-sm text-ink/75 hover:text-ink hover:bg-sand transition-colors duration-200 border-t border-ink/10 mt-1"
                          >
                            {e.label}
                          </Link>
                        ))}
                        {area && (
                          <Link to={item.to} className="block px-4 py-2.5 text-[11px] uppercase tracking-[0.15em] text-sagedark border-t border-ink/10 mt-1">
                            Panoramica →
                          </Link>
                        )}
                      </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-booking-button"
            onClick={() => navigate("/contatti")}
            className="group relative hidden sm:inline-flex items-center overflow-hidden border border-ink px-6 py-2.5 text-xs uppercase tracking-[0.15em]"
          >
            <span className="absolute inset-0 bg-ink translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]" />
            <span className="relative group-hover:text-canvas transition-colors duration-500">Prenota</span>
          </button>
          <button data-testid="nav-mobile-toggle" onClick={() => setOpen((v) => !v)} className="xl:hidden p-2 -mr-2" aria-label="Menu">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="xl:hidden overflow-hidden bg-canvas/97 backdrop-blur-xl border-t border-ink/10"
          >
            <div className="px-6 py-6 flex flex-col gap-1 max-h-[75vh] overflow-y-auto">
              <Link to="/" onClick={() => setOpen(false)} className="font-serif text-2xl py-2">Home</Link>
              {NAV.map((l) => (
                <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="font-serif text-2xl py-2">{l.label}</Link>
              ))}
              <button onClick={() => { setOpen(false); navigate("/contatti"); }} className="mt-3 border border-ink px-6 py-3 text-xs uppercase tracking-[0.15em] text-center">
                Prenota una valutazione
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
